import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  StarIcon,
  CogIcon,
  HomeIcon,
  BellIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/solid';

interface StudentData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  instrument: string;
  skillLevel: string;
  genres: string[];
  learningGoals: string[];
  lessonLocation: string[];
  age: string;
  learnerType: 'learner' | 'parent';
  guardianInfo?: {
    name: string;
    email: string;
    phone: string;
  };
}

interface StudentLessonRequest {
  id: string;
  teacherName: string;
  teacherRating: number;
  instrument: string;
  skillLevel: string;
  requestedDate: string;
  requestedTime: string;
  lessonType: string;
  duration: number;
  specialRequests?: string;
  requestDate: string;
  status: 'pending' | 'approved' | 'declined';
  hourlyRate: number;
  referenceNumber?: string;
}

interface StudentLesson {
  id: string;
  teacherName: string;
  teacherRating: number;
  instrument: string;
  date: string;
  time: string;
  duration: number;
  lessonType: string;
  totalPrice: number;
  status: 'upcoming' | 'completed' | 'cancelled';
  lessonNotes?: string;
  homework?: string;
}

// Mock data for student lesson requests
const mockStudentRequests: StudentLessonRequest[] = [
  {
    id: '1',
    teacherName: 'Sarah Johnson',
    teacherRating: 4.9,
    instrument: 'Piano',
    skillLevel: 'Beginner',
    requestedDate: 'Monday',
    requestedTime: '4:00 PM',
    lessonType: 'Online',
    duration: 30,
    specialRequests: 'Would like to focus on classical pieces and basic music theory',
    requestDate: '2 hours ago',
    status: 'pending',
    hourlyRate: 75,
    referenceNumber: 'LLX1641234567'
  },
  {
    id: '2',
    teacherName: 'Michael Rodriguez',
    teacherRating: 4.8,
    instrument: 'Piano',
    skillLevel: 'Beginner',
    requestedDate: 'Wednesday',
    requestedTime: '5:00 PM',
    lessonType: 'On-site (teacher\'s studio)',
    duration: 60,
    specialRequests: 'Looking to learn jazz fundamentals',
    requestDate: '1 day ago',
    status: 'approved',
    hourlyRate: 75,
    referenceNumber: 'LLX1641234568'
  },
  {
    id: '3',
    teacherName: 'Emily Chen',
    teacherRating: 4.7,
    instrument: 'Voice/Vocals',
    skillLevel: 'Beginner',
    requestedDate: 'Friday',
    requestedTime: '6:00 PM',
    lessonType: 'Online',
    duration: 30,
    requestDate: '3 days ago',
    status: 'declined',
    hourlyRate: 70
  }
];

// Mock data for student lessons
const mockStudentLessons: StudentLesson[] = [
  {
    id: '1',
    teacherName: 'Michael Rodriguez',
    teacherRating: 4.8,
    instrument: 'Piano',
    date: 'Today',
    time: '5:00 PM',
    duration: 60,
    lessonType: 'On-site (teacher\'s studio)',
    totalPrice: 75.00,
    status: 'upcoming'
  },
  {
    id: '2',
    teacherName: 'Sarah Johnson',
    teacherRating: 4.9,
    instrument: 'Piano',
    date: 'Next Monday',
    time: '4:00 PM',
    duration: 30,
    lessonType: 'Online',
    totalPrice: 37.50,
    status: 'upcoming'
  },
  {
    id: '3',
    teacherName: 'David Kim',
    teacherRating: 4.6,
    instrument: 'Piano',
    date: 'Last Tuesday',
    time: '3:00 PM',
    duration: 30,
    lessonType: 'Online',
    totalPrice: 35.00,
    status: 'completed',
    lessonNotes: 'Great progress on scales! Keep practicing daily.',
    homework: 'Practice C major scale 10 minutes daily, work on "Ode to Joy" first 8 measures'
  }
];

const StudentDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { studentData } = location.state || {};
  const [activeTab, setActiveTab] = useState('overview');
  const [lessonRequests] = useState<StudentLessonRequest[]>(mockStudentRequests);
  const [lessons] = useState<StudentLesson[]>(mockStudentLessons);

  // Log dashboard access
  console.log('Student dashboard loaded for:', studentData);

  // Mock student data if not provided
  const mockStudentData = studentData || {
    firstName: 'Emma',
    lastName: 'Thompson',
    email: 'emma.thompson@example.com',
    phoneNumber: '(555) 987-6543',
    instrument: 'Piano',
    skillLevel: 'Beginner',
    genres: ['Classical', 'Pop'],
    learningGoals: ['Learn to read sheet music', 'Play favorite songs'],
    lessonLocation: ['Online', 'At the teacher\'s studio'],
    age: '12 years old',
    learnerType: 'learner' as const,
    guardianInfo: {
      name: 'Sarah Thompson',
      email: 'sarah.thompson@example.com',
      phone: '(555) 123-4567'
    }
  };

  const pendingRequestsCount = lessonRequests.filter(req => req.status === 'pending').length;
  const upcomingLessonsCount = lessons.filter(lesson => lesson.status === 'upcoming').length;
  const totalSpent = lessons
    .filter(lesson => lesson.status === 'completed')
    .reduce((total, lesson) => total + lesson.totalPrice, 0);

  const handleContactTeacher = (teacherName: string) => {
    console.log('Opening contact with teacher:', teacherName);
    navigate('/messages', { 
      state: { 
        openConversationWith: teacherName,
        userType: 'teacher'
      }
    });
  };

  const handleCancelRequest = (requestId: string) => {
    console.log('Cancelling lesson request:', requestId);
    // TODO: Implement request cancellation
    alert('Request cancellation feature coming soon!');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: HomeIcon },
    { id: 'requests', label: `Requests (${pendingRequestsCount})`, icon: BellIcon },
    { id: 'lessons', label: 'My Lessons', icon: CalendarDaysIcon },
    { id: 'payments', label: 'Payments', icon: CurrencyDollarIcon },
    { id: 'messages', label: 'Messages', icon: ChatBubbleLeftRightIcon },
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'settings', label: 'Settings', icon: CogIcon }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Welcome back, {mockStudentData.firstName}!
              </h2>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BellIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                    <p className="text-2xl font-semibold text-gray-900">{pendingRequestsCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CalendarDaysIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Upcoming Lessons</p>
                    <p className="text-2xl font-semibold text-gray-900">{upcomingLessonsCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <AcademicCapIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Lessons Completed</p>
                    <p className="text-2xl font-semibold text-gray-900">{lessons.filter(l => l.status === 'completed').length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {lessonRequests.slice(0, 3).map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <UserIcon className="w-8 h-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{request.teacherName}</p>
                          <p className="text-sm text-gray-600">
                            {request.instrument} lesson • {request.requestDate}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Next Lesson */}
            {upcomingLessonsCount > 0 && (
              <div className="bg-white rounded-lg shadow border">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Next Lesson</h3>
                </div>
                <div className="p-6">
                  {(() => {
                    const nextLesson = lessons.find(lesson => lesson.status === 'upcoming');
                    return nextLesson ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <AcademicCapIcon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{nextLesson.teacherName}</h4>
                            <p className="text-sm text-gray-600">
                              {nextLesson.instrument} • {nextLesson.duration} minutes • {nextLesson.lessonType}
                            </p>
                            <p className="text-sm text-gray-600">
                              {nextLesson.date} at {nextLesson.time}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">${nextLesson.totalPrice.toFixed(2)}</p>
                          <button
                            onClick={() => handleContactTeacher(nextLesson.teacherName)}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Message Teacher
                          </button>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            )}
          </div>
        );

      case 'requests':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Lesson Requests</h2>
              <p className="text-gray-600">Track the status of your lesson requests and manage them.</p>
            </div>

            <div className="space-y-4">
              {lessonRequests.map((request) => (
                <div key={request.id} className="bg-white rounded-lg shadow border overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{request.teacherName}</h3>
                            <div className="flex items-center space-x-1">
                              <StarIcon className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm text-gray-600">{request.teacherRating}</span>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {request.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Instrument</p>
                              <p className="text-sm text-gray-900">{request.instrument}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Date & Time</p>
                              <p className="text-sm text-gray-900">{request.requestedDate}, {request.requestedTime}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Duration & Price</p>
                              <p className="text-sm text-gray-900">
                                {request.duration} min • ${(request.duration === 30 ? request.hourlyRate / 2 : request.hourlyRate).toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Lesson Type</p>
                              <p className="text-sm text-gray-900">{request.lessonType}</p>
                            </div>
                          </div>

                          {request.specialRequests && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-600 mb-1">Special Requests:</p>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{request.specialRequests}</p>
                            </div>
                          )}

                          {request.referenceNumber && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-600">Reference Number:</p>
                              <p className="text-sm text-gray-900 font-mono">{request.referenceNumber}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <div className="text-sm text-gray-500">
                        Requested {request.requestDate}
                      </div>
                      <div className="flex space-x-3">
                        {request.status === 'pending' && (
                          <button
                            onClick={() => handleCancelRequest(request.id)}
                            className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <XMarkIcon className="w-4 h-4" />
                            <span>Cancel Request</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleContactTeacher(request.teacherName)}
                          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          <span>Message Teacher</span>
                        </button>
                      </div>
                    </div>

                    {request.status === 'approved' && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-green-700">
                          <CheckCircleIcon className="w-5 h-5" />
                          <span className="font-medium">Request Approved!</span>
                        </div>
                        <p className="text-sm text-green-600 mt-1">
                          Your lesson has been confirmed. The teacher will contact you soon with next steps.
                        </p>
                      </div>
                    )}

                    {request.status === 'declined' && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-red-700">
                          <ExclamationCircleIcon className="w-5 h-5" />
                          <span className="font-medium">Request Declined</span>
                        </div>
                        <p className="text-sm text-red-600 mt-1">
                          This teacher is not available for the requested time. Try browsing other teachers or different times.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {lessonRequests.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No lesson requests</h3>
                  <p className="text-gray-600 mb-4">Start by browsing teachers and requesting your first lesson!</p>
                  <button
                    onClick={() => navigate('/browse-teachers')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Browse Teachers
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'lessons':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Lessons</h2>
              <p className="text-gray-600">View your upcoming and completed lessons.</p>
            </div>

            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Lessons</h3>
              </div>
              <div className="divide-y">
                {lessons
                  .filter(lesson => lesson.status === 'upcoming')
                  .map((lesson) => (
                  <div key={lesson.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{lesson.teacherName}</h4>
                            <div className="flex items-center space-x-1">
                              <StarIcon className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm text-gray-600">{lesson.teacherRating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600">
                            {lesson.instrument} • {lesson.duration} minutes • {lesson.lessonType}
                          </p>
                          <p className="text-sm text-gray-600">
                            {lesson.date} at {lesson.time}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${lesson.totalPrice.toFixed(2)}</p>
                        <button
                          onClick={() => handleContactTeacher(lesson.teacherName)}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Message
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {lessons.filter(lesson => lesson.status === 'upcoming').length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    No upcoming lessons scheduled
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Completed Lessons</h3>
              </div>
              <div className="divide-y">
                {lessons
                  .filter(lesson => lesson.status === 'completed')
                  .map((lesson) => (
                  <div key={lesson.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{lesson.teacherName}</h4>
                            <div className="flex items-center space-x-1">
                              <StarIcon className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm text-gray-600">{lesson.teacherRating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {lesson.instrument} • {lesson.duration} minutes • {lesson.lessonType}
                          </p>
                          <p className="text-sm text-gray-600 mb-3">
                            {lesson.date} at {lesson.time}
                          </p>
                          
                          {lesson.lessonNotes && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-1">Lesson Notes:</p>
                              <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">{lesson.lessonNotes}</p>
                            </div>
                          )}
                          
                          {lesson.homework && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">Homework:</p>
                              <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg">{lesson.homework}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-green-600">${lesson.totalPrice.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Completed</p>
                      </div>
                    </div>
                  </div>
                ))}
                {lessons.filter(lesson => lesson.status === 'completed').length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    No completed lessons yet
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment History</h2>
              <p className="text-gray-600">View your payment history and manage payment methods.</p>
            </div>

            {/* Payment Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Spent</p>
                    <p className="text-2xl font-semibold text-gray-900">${totalSpent.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CalendarDaysIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">This Month</p>
                    <p className="text-2xl font-semibold text-gray-900">$0.00</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CheckCircleIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Payments Made</p>
                    <p className="text-2xl font-semibold text-gray-900">{lessons.filter(l => l.status === 'completed').length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Payments */}
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Recent Payments</h3>
              </div>
              <div className="divide-y">
                {lessons
                  .filter(lesson => lesson.status === 'completed')
                  .map((lesson) => (
                  <div key={lesson.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{lesson.teacherName}</h4>
                          <p className="text-sm text-gray-600">
                            {lesson.instrument} lesson • {lesson.duration} minutes
                          </p>
                          <p className="text-sm text-gray-600">
                            {lesson.date} at {lesson.time}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">${lesson.totalPrice.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Paid</p>
                        <button
                          onClick={() => {
                            console.log('Downloading receipt for lesson:', lesson.id);
                            alert('Receipt download feature coming soon!');
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Download Receipt
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {lessons.filter(lesson => lesson.status === 'completed').length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    No payment history yet
                  </div>
                )}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg">💳</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">•••• •••• •••• 4242</p>
                        <p className="text-sm text-gray-600">Expires 12/25</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className="text-sm text-blue-600 hover:text-blue-700">Edit</button>
                      <button className="text-sm text-red-600 hover:text-red-700">Remove</button>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    + Add New Payment Method
                  </button>
                </div>
              </div>
            </div>

            {/* Billing Information */}
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Billing Information</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Billing Email</p>
                    <p className="text-sm text-gray-900">{mockStudentData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Billing Address</p>
                    <p className="text-sm text-gray-900">123 Main St, City, State 12345</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button className="px-4 py-2 text-blue-600 hover:text-blue-700">
                    Update Billing Information
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Messages</h2>
              <p className="text-gray-600">Communicate with your teachers.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-8 text-center">
              <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Access Full Messaging</h3>
              <p className="text-gray-600 mb-6">
                Use our dedicated messaging interface to communicate with your teachers.
              </p>
              <button
                onClick={() => navigate('/messages')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Messages
              </button>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Information</h2>
              <p className="text-gray-600">View and edit your learning profile.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Name</p>
                      <p className="text-sm text-gray-900">{mockStudentData.firstName} {mockStudentData.lastName}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Email</p>
                      <p className="text-sm text-gray-900">{mockStudentData.email}</p>
                    </div>
                    {mockStudentData.phoneNumber && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Phone</p>
                        <p className="text-sm text-gray-900">{mockStudentData.phoneNumber}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-600">Age</p>
                      <p className="text-sm text-gray-900">{mockStudentData.age}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Profile</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Primary Instrument</p>
                      <p className="text-sm text-gray-900">{mockStudentData.instrument}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Skill Level</p>
                      <p className="text-sm text-gray-900">{mockStudentData.skillLevel}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Genres</p>
                      <p className="text-sm text-gray-900">{mockStudentData.genres.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Lesson Preferences</p>
                      <p className="text-sm text-gray-900">{mockStudentData.lessonLocation.join(', ')}</p>
                    </div>
                  </div>
                </div>

                {mockStudentData.guardianInfo && (
                  <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Guardian Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Guardian Name</p>
                        <p className="text-sm text-gray-900">{mockStudentData.guardianInfo.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Guardian Email</p>
                        <p className="text-sm text-gray-900">{mockStudentData.guardianInfo.email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Guardian Phone</p>
                        <p className="text-sm text-gray-900">{mockStudentData.guardianInfo.phone}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t">
                <button
                  onClick={() => navigate('/new-student/questions', { state: { edit: true, studentData: mockStudentData } })}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Settings</h2>
              <p className="text-gray-600">Manage your account preferences and settings.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Email notifications</p>
                    <p className="text-sm text-gray-600">Receive email alerts for lesson updates and messages</p>
                  </div>
                  <input type="checkbox" defaultChecked className="rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">SMS notifications</p>
                    <p className="text-sm text-gray-600">Receive text messages for lesson reminders</p>
                  </div>
                  <input type="checkbox" className="rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Lesson reminders</p>
                    <p className="text-sm text-gray-600">Get reminded 24 hours before your lesson</p>
                  </div>
                  <input type="checkbox" defaultChecked className="rounded" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account</h3>
              <div className="space-y-4">
                <button className="text-blue-600 hover:text-blue-700">Change Password</button>
                <button className="text-red-600 hover:text-red-700">Deactivate Account</button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-semibold text-gray-900">Student Dashboard</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {mockStudentData.firstName} {mockStudentData.lastName}
                </span>
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <HomeIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default StudentDashboardPage; 